import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Camera } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CaptionPanel() {
  const [list, setList] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const today = new Date().toISOString().slice(0,10);
  const [form, setForm] = useState({ active_date: today, title: "", file: null });

  const refresh = () => api.get("/admin/caption/contests").then((r) => setList(r.data));
  useEffect(() => { refresh(); }, []);

  const create = async () => {
    if (!form.file) { toast.error("Pick an image"); return; }
    const fd = new FormData();
    fd.append("active_date", form.active_date);
    fd.append("title", form.title);
    fd.append("file", form.file);
    try {
      await api.post("/admin/caption/contests", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Contest created");
      setOpenNew(false);
      setForm({ active_date: today, title: "", file: null });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const remove = async (cid) => {
    if (!confirm("Delete this contest and all its captions?")) return;
    await api.delete(`/admin/caption/contests/${cid}`);
    toast.success("Deleted");
    refresh();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-black uppercase flex items-center gap-2"><Camera size={18}/> Caption Contests</h3>
        <button data-testid="caption-new-btn" onClick={() => setOpenNew(true)} className="brutal-btn inline-flex items-center gap-1">
          <Plus size={14}/> New Contest
        </button>
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-neutral-600">{`No caption contests yet. Click "New Contest" to upload a photo.`}</p>
      ) : (
        <div className="grid gap-2 md:grid-cols-2">
          {list.map((c) => {
            const isToday = c.active_date === today;
            return (
              <div key={c.id} data-testid={`caption-row-${c.id}`} className="border-2 border-black p-3 flex gap-3" style={{background: isToday ? "#FFFCE5" : "white"}}>
                <div className="w-24 h-24 border-2 border-black overflow-hidden flex-shrink-0">
                  <img src={`${BACKEND_URL}/api/files/${c.image_path}`} alt="" className="w-full h-full object-cover"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs uppercase font-mono-bq mb-1">
                    <span className="border border-black px-1.5 py-0.5">{c.active_date}</span>
                    {isToday && <span className="border border-black px-1.5 py-0.5 bg-black text-white">LIVE TODAY</span>}
                  </div>
                  <div className="font-black truncate">{c.title || "(no title)"}</div>
                  <div className="text-xs text-neutral-600 mt-1">{c.submission_count} captions</div>
                  <button onClick={() => remove(c.id)} data-testid={`caption-del-${c.id}`} className="brutal-btn text-xs mt-2 inline-flex items-center gap-1">
                    <Trash2 size={12}/> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="border-4 border-black rounded-none max-w-md">
          <DialogHeader><DialogTitle className="uppercase font-black">New Caption Contest</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="block text-xs uppercase font-black">Active date</label>
            <input data-testid="caption-form-date" type="date" value={form.active_date} onChange={(e) => setForm({ ...form, active_date: e.target.value })} className="border-2 border-black p-2 w-full"/>
            <label className="block text-xs uppercase font-black">Title (optional)</label>
            <input data-testid="caption-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="border-2 border-black p-2 w-full" placeholder="e.g. CEO at the bowling alley"/>
            <label className="block text-xs uppercase font-black">Photo (PNG/JPG/WebP, ≤4 MB)</label>
            <input data-testid="caption-form-file" type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] })} className="block w-full text-sm"/>
          </div>
          <DialogFooter>
            <button data-testid="caption-form-save" onClick={create} className="brutal-btn">Create</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CaptionPanel;
